
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye } from 'lucide-react';

type FeedbackData = {
  id: string;
  candidate_name: string;
  job_title: string;
  created_at: string;
  certified: boolean;
  user_id: string;
  recruiter_name?: string | null;
  feedback_type?: string | null;
};

interface FeedbackRequestData {
  feedback_id: string;
  recruiter_user_id?: string | null;
}

type FeedbacksListProps = {
  candidateEmail?: string;
};

export const FeedbacksList = ({ candidateEmail }: FeedbacksListProps) => {
  const [feedbacks, setFeedbacks] = useState<FeedbackData[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const effectiveEmail = candidateEmail || user?.email;
  const isRecruiter = profile?.role === 'entreprise';

  useEffect(() => {
    if (effectiveEmail) {
      fetchFeedbacks();
    }
  }, [effectiveEmail]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      let allFeedbacks: FeedbackData[] = [];
      const email = effectiveEmail?.toLowerCase();

      if (!email) {
        setFeedbacks([]);
        setLoading(false);
        return;
      }

      console.log("Recherche de feedbacks pour:", email);

      // 1. Recherche directe dans feedbacks via email
      const { data: emailFeedbacks, error: emailError } = await supabase
        .from('feedbacks')
        .select('id, candidate_name, job_title, created_at, certified, user_id, feedback_type')
        .ilike('candidate_email', email)
        .order('created_at', { ascending: false });

      if (emailError) {
        console.error("Erreur lors de la recherche email:", emailError);
      } else if (emailFeedbacks && emailFeedbacks.length > 0) {
        allFeedbacks = [...emailFeedbacks as FeedbackData[]];
      }

      if (isRecruiter && user?.id) {
        // 2. Pour les recruteurs, rechercher également dans candidate_feedbacks
        console.log("Recherche des feedbacks des candidats dans candidate_feedbacks pour le recruteur:", user.id);
        
        const { data: recruiterfeedbacks, error: recruiterFeedbacksError } = await supabase
          .from('candidate_feedbacks')
          .select('id, created_at, feedback_text, rating, candidate_user_id')
          .eq('recruiter_user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (recruiterFeedbacksError) {
          console.error("Erreur lors de la récupération des feedbacks de candidats:", recruiterFeedbacksError);
        } else if (recruiterfeedbacks && recruiterfeedbacks.length > 0) {
          console.log(`Trouvé ${recruiterfeedbacks.length} feedbacks de candidats pour le recruteur`);
          
          // Pour chaque feedback, récupérer les informations du candidat
          const enrichedRecruiterFeedbacks = await Promise.all(
            recruiterfeedbacks.map(async (fb) => {
              let candidateName = 'Candidat';
              let jobTitle = 'Non spécifié';
              
              // Récupérer les infos du candidat si disponible
              if (fb.candidate_user_id) {
                const { data: candidateProfile } = await supabase
                  .from('profiles')
                  .select('full_name, email')
                  .eq('id', fb.candidate_user_id)
                  .maybeSingle();
                  
                if (candidateProfile) {
                  candidateName = candidateProfile.full_name || candidateProfile.email || 'Candidat';
                }
              }
              
              // Essayer d'extraire les données JSON du feedback_text
              try {
                const feedbackData = JSON.parse(fb.feedback_text || '{}');
                if (feedbackData.requestData?.job_title) {
                  jobTitle = feedbackData.requestData.job_title;
                } else if (feedbackData.process) {
                  jobTitle = feedbackData.process;
                }
              } catch (e) {
                console.warn("Impossible de parser le feedback_text:", e);
              }
              
              // Construire l'objet feedback
              return {
                id: fb.id,
                candidate_name: candidateName,
                job_title: jobTitle,
                created_at: fb.created_at,
                certified: true,
                user_id: '',
                recruiter_name: profile?.full_name || user.email || 'Recruteur',
                feedback_type: 'recruiter_feedback'
              } as FeedbackData;
            })
          );
          
          allFeedbacks = [...allFeedbacks, ...enrichedRecruiterFeedbacks];
        }
      } else if (!isRecruiter && user?.id) {
        // 2. Recherche dans candidate_feedbacks pour les feedbacks de recruteurs (pour candidats)
        const { data: candidateFeedbacks, error: candidateFeedbacksError } = await supabase
          .from('candidate_feedbacks')
          .select('id, created_at, recruiter_user_id, candidate_user_id')
          .eq('candidate_user_id', user.id)
          .order('created_at', { ascending: false });

        if (!candidateFeedbacksError && candidateFeedbacks?.length) {
          console.log(`Trouvé ${candidateFeedbacks.length} feedbacks de recruteurs`);
          
          // Pour chaque feedback, récupérer les infos du recruteur
          const enrichedCandidateFeedbacks = await Promise.all(
            candidateFeedbacks.map(async (fb) => {
              // Récupérer les informations du recruteur
              let recruiterName = 'Recruteur';
              let recruiterCompany = '';
              let jobTitle = 'Non spécifié';
              
              if (fb.recruiter_user_id) {
                const { data: recruiterProfile } = await supabase
                  .from('profiles')
                  .select('full_name, organization')
                  .eq('id', fb.recruiter_user_id)
                  .maybeSingle();
                  
                if (recruiterProfile) {
                  recruiterName = recruiterProfile.full_name || 'Recruteur';
                  recruiterCompany = recruiterProfile.organization || '';
                }
              }
              
              // Essayer de récupérer des informations supplémentaires depuis feedback_requests
              const { data: requestData } = await supabase
                .from('candidate_to_recruiter_feedback_requests')
                .select('job_title')
                .or(`candidate_user_id.eq.${user.id},candidate_email.eq.${email}`)
                .eq('feedback_id', fb.id)
                .maybeSingle();
                
              if (requestData?.job_title) {
                jobTitle = requestData.job_title;
              }
              
              // Construire l'objet de feedback
              return {
                id: fb.id,
                candidate_name: user?.email || email || 'Candidat',
                recruiter_name: recruiterName,
                job_title: jobTitle,
                created_at: fb.created_at,
                certified: true,
                user_id: fb.recruiter_user_id || '',
                feedback_type: 'candidat'
              } as FeedbackData;
            })
          );
          
          allFeedbacks = [...allFeedbacks, ...enrichedCandidateFeedbacks];
        }
      }

      // 3. Recherche via feedback_requests par email
      const { data: requestsData, error: requestsError } = await supabase
        .from('feedback_requests')
        .select('feedback_id, recruiter_user_id')
        .ilike('candidate_email', email)
        .not('feedback_id', 'is', null);

      if (requestsError) {
        console.error("Erreur via feedback_requests:", requestsError);
      } else if (requestsData?.length) {
        // Type predicate function to validate requests
        const isValidRequest = (req: any): req is FeedbackRequestData => {
          return req && typeof req.feedback_id === 'string';
        };
        
        const feedbackIds = requestsData
          .filter(isValidRequest)
          .map(req => req.feedback_id);

        if (feedbackIds.length > 0) {
          const { data: feedbackData, error: feedbackError } = await supabase
            .from('feedbacks')
            .select('id, candidate_name, job_title, created_at, certified, user_id, feedback_type')
            .in('id', feedbackIds)
            .order('created_at', { ascending: false });

          if (!feedbackError && feedbackData?.length) {
            const existingIds = new Set(allFeedbacks.map(f => f.id));
            const newFeedbacks = feedbackData.filter(f => !existingIds.has(f.id));
            allFeedbacks = [...allFeedbacks, ...newFeedbacks];
          }
        }
      }

      // 4. Enrichir avec le nom du recruteur
      const enrichedFeedbacks = await Promise.all(
        allFeedbacks.map(async (fb) => {
          if (fb.user_id) {
            const { data: userData } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', fb.user_id)
              .maybeSingle();
            return {
              ...fb,
              recruiter_name: userData?.full_name || 'Recruteur',
            };
          }
          return fb;
        })
      );

      setFeedbacks(enrichedFeedbacks);
    } catch (error: any) {
      console.error('Erreur fetchFeedbacks:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Échec lors du chargement des feedbacks',
        variant: 'destructive',
      });
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFeedback = (id: string, feedbackType?: string) => {
    // Déterminer si c'est un feedback normal ou un feedback de candidat
    const feedback = feedbacks.find(f => f.id === id);
    
    if (feedback?.feedback_type === 'candidat' || feedback?.feedback_type === 'recruiter_feedback') {
      navigate(`/view-candidate-feedback/${id}`);
    } else {
      navigate(`/view-feedback/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
      </div>
    );
  }

  if (!feedbacks.length) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Feedbacks reçus</CardTitle>
          <CardDescription>Aucun feedback n'a été trouvé pour cette adresse email.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            Les feedbacks que vous recevrez apparaîtront ici.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Feedbacks reçus</CardTitle>
        <CardDescription>Liste des feedbacks reçus {isRecruiter ? 'des candidats' : 'des recruteurs'}</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>De</TableHead>
              <TableHead>Poste</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feedbacks.map((fb) => (
              <TableRow key={fb.id}>
                <TableCell>{fb.feedback_type === 'recruiter_feedback' ? fb.candidate_name : fb.recruiter_name}</TableCell>
                <TableCell>{fb.job_title}</TableCell>
                <TableCell>{new Date(fb.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Badge variant={fb.certified ? 'default' : 'outline'}>
                    {fb.certified ? 'Certifié' : 'Non certifié'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleViewFeedback(fb.id, fb.feedback_type)}>
                    <Eye size={16} className="mr-1" /> Voir
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default FeedbacksList;
